// A media tile. With `src` it renders a real image or video (from R2); without it falls
// back to an abstract watercolor wash placeholder (used by the prototype + loading states).
import type { CSSProperties } from "react";
import { washFor } from "../lib/content";

interface PhotoTileProps {
  seed?: number;
  src?: string;
  /** Render as a <video> instead of <img>. */
  video?: boolean;
  /** Show native video controls (lightbox). Off in grids — shows first frame + play badge. */
  controls?: boolean;
  ratio?: string;
  radius?: number;
  className?: string;
  style?: CSSProperties;
  onClick?: () => void;
  label?: string;
}

export function PhotoTile({
  seed = 0,
  src,
  video = false,
  controls = false,
  ratio = "1 / 1",
  radius = 4,
  className = "",
  style = {},
  onClick,
  label,
}: PhotoTileProps) {
  return (
    <div
      className={"ptile " + className}
      onClick={onClick}
      style={{ aspectRatio: ratio, borderRadius: radius, ...(src ? {} : washFor(seed)), ...style }}
    >
      {src && video ? (
        <video
          className="ptile-img"
          // #t=0.1 makes the browser render a real frame as the poster instead of a black box.
          src={`${src}#t=0.1`}
          muted={!controls}
          controls={controls}
          playsInline
          preload="metadata"
        />
      ) : src ? (
        <img className="ptile-img" src={src} alt={label || ""} loading="lazy" />
      ) : null}
      {/* Decorative overlays — never over a video (they'd cover its controls / play button). */}
      {!video && (
        <>
          <div className="ptile-grain" />
          <div className="ptile-vignette" />
        </>
      )}
      {video && !controls && (
        <div className="ptile-play" aria-hidden="true">
          <svg viewBox="0 0 24 24" width="38" height="38">
            <circle cx="12" cy="12" r="11" fill="rgba(0,0,0,0.42)" />
            <path d="M9.5 7.8 16 12l-6.5 4.2z" fill="#fff" />
          </svg>
        </div>
      )}
      {label && <div className="ptile-label">{label}</div>}
    </div>
  );
}
